import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import apiService from "../../../services/api";
import { getInstituicaoLogo } from "../../../utils/instituicaoLogos";
import { formatCPF } from "../../../utils/formatters";
import emAnaliseIcon from "../../../assets/em-analise.png";
import concedidoIcon from "../../../assets/concedido.png";
import naoConcedidoIcon from "../../../assets/naoConcedido.png";
import { usePopup } from "../../common/Popup";

const COLORS = {
  primary: "#6ab8a8",
  primaryDark: "#008c7a",
  primaryLight: "#e8f5f3",
  text: "#555555",
  dark: "#002e3b",
  border: "#dfe3e8",
  orange: "#ff9f1f",
  green: "#059669",
  red: "#dc2626",
};

const Wrapper = styled.div`
  padding: 0 0 40px;
  font-family: "Public Sans", sans-serif;
  background: #fff;
`;

const PageHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
  background: #f2f0f0;
  border-top: 1px solid #cccccc;
  border-bottom: 1px solid #dddddd;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderTitle = styled.h2`
  min-width: 0;
  margin: 0;
  padding: 12px 22px;
  color: ${COLORS.primary};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &:first-child {
    border-right: 1px solid #e0e0e0;
  }

  @media (max-width: 980px) {
    &:first-child {
      border-right: 0;
      border-bottom: 1px solid #dddddd;
    }
  }
`;

const RequestCount = styled.span`
  margin-left: 8px;
  color: ${COLORS.orange};
`;

const DetailLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
  align-items: stretch;
  min-height: calc(100vh - 122px);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.section`
  min-width: 0;

  &:first-child {
    border-right: 1px solid #e0e0e0;
  }

  &:last-child {
    padding: 16px 24px 20px;
  }

  @media (max-width: 980px) {
    &:first-child {
      border-right: 0;
      border-bottom: 1px solid #e0e0e0;
    }
  }
`;

const VagaCard = styled.div`
  overflow: hidden;
  background: #fff;
`;

const VagaTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 22px;
  background: ${COLORS.primaryLight};
  border-bottom: 1px solid ${COLORS.border};
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: ${COLORS.primary};
  font-size: 13px;
  font-weight: 700;
`;

const UnitText = styled.div`
  min-width: 0;
`;

const UnitLabel = styled.div`
  margin-bottom: 4px;
  color: ${COLORS.primaryDark};
  font-size: 16px;
  font-weight: 600;
`;

const UnitName = styled.div`
  overflow: hidden;
  color: ${COLORS.text};
  font-size: 16px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const VagaField = styled.div`
  display: grid;
  grid-template-columns: minmax(112px, 42%) minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-height: 42px;
  padding: 10px 22px;
  border-bottom: 1px solid #efefef;

  &:last-child {
    border-bottom: 0;
  }
`;

const FieldLabel = styled.div`
  color: ${COLORS.text};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
`;

const FieldValue = styled.div`
  min-width: 0;
  color: ${COLORS.text};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const TotalsCard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 16px;
  padding: 18px 10px;
  background: #fff;
  border-top: 1px solid ${COLORS.border};
  border-bottom: 1px solid ${COLORS.border};
`;

const TotalItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${COLORS.dark};
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;

  strong {
    color: ${({ $color }) => $color};
    font-size: 22px;
    line-height: 1;
  }
`;

const RequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PlanoEnsinoButton = styled.button`
  display: block;
  width: fit-content;
  margin-left: auto;
  background: ${COLORS.orange};
  color: #ffffff;
  border: none;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #e89010;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`;

const RequestCard = styled.article`
  overflow: hidden;
  background: #fff;
  border: 1px solid ${({ $expanded }) => ($expanded ? "#8adaca" : COLORS.border)};
  border-radius: 0;
  transition: border-color 0.2s;

  &:hover {
    border-color: #b8d8d2;
  }
`;

const RequestSummary = styled.div`
  display: grid;
  grid-template-columns:
    minmax(280px, 2fr)
    minmax(110px, 0.75fr)
    minmax(70px, 0.4fr)
    minmax(135px, 0.8fr)
    36px;
  gap: 14px;
  align-items: center;
  min-height: 64px;
  padding: 10px 14px;
  background: ${({ $expanded }) => ($expanded ? COLORS.primaryLight : "#fff")};

  @media (max-width: 1180px) {
    grid-template-columns:
      minmax(260px, 1.8fr)
      minmax(110px, 0.75fr)
      minmax(70px, 0.4fr)
      36px;
  }

  @media (max-width: 680px) {
    grid-template-columns: minmax(0, 1fr) 36px;
    gap: 10px;
  }
`;

const InstitutionSummary = styled.div`
  display: grid;
  grid-template-columns: 26px 36px minmax(0, 1fr);
  gap: 9px;
  align-items: center;
  min-width: 0;
`;

const StatusIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const LogoWrapper = styled.div`
  width: 36px;
  height: 36px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  background: ${COLORS.primary};
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const InstitutionText = styled.div`
  min-width: 0;
`;

const InstitutionName = styled.div`
  overflow: hidden;
  color: ${COLORS.text};
  font-size: 15px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CourseLine = styled.div`
  overflow: hidden;
  margin-top: 4px;
  color: ${COLORS.text};
  font-size: 13px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SummaryField = styled.div`
  min-width: 0;

  @media (max-width: 680px) {
    display: none;
  }

  &.request-date {
    @media (max-width: 1180px) {
      display: none;
    }
  }
`;

const SummaryLabel = styled.div`
  margin-bottom: 4px;
  color: ${COLORS.text};
  font-size: 13px;
`;

const SummaryValue = styled.div`
  overflow: hidden;
  color: ${COLORS.text};
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExpandButton = styled.button`
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $expanded }) => ($expanded ? COLORS.primaryDark : COLORS.dark)};
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(106, 184, 168, 0.14);
    outline: none;
  }

  span {
    font-size: 24px;
    transform: rotate(${({ $expanded }) => ($expanded ? "180deg" : "0deg")});
    transition: transform 0.2s;
  }
`;

const RequestBody = styled.div`
  padding: 20px;
  border-top: 1px solid #bfe6df;

  @media (max-width: 680px) {
    padding: 18px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailField = styled.div`
  min-height: 78px;
  padding: 4px 20px 16px;
  border-right: 1px solid ${COLORS.border};

  &:nth-child(3n) {
    border-right: 0;
  }

  @media (max-width: 720px) {
    &:nth-child(3n) {
      border-right: 1px solid ${COLORS.border};
    }

    &:nth-child(2n) {
      border-right: 0;
    }
  }

  @media (max-width: 480px) {
    min-height: auto;
    padding: 8px 0 14px;
    border-right: 0 !important;
  }
`;

const DetailLabel = styled.div`
  margin-bottom: 8px;
  color: ${COLORS.text};
  font-size: 14px;
  font-weight: 700;
`;

const DetailValue = styled.div`
  color: ${COLORS.text};
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const DecisionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(230px, 1fr) minmax(170px, 0.8fr) auto;
  gap: 22px;
  align-items: end;
  margin-top: 4px;
  padding: 20px 20px 4px;
  border-top: 1px solid ${COLORS.border};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0;

  button,
  input {
    width: 38px;
    height: 34px;
    padding: 0;
    color: ${COLORS.dark};
    background: #fff;
    border: 1px solid ${COLORS.border};
    text-align: center;
  }

  button {
    cursor: pointer;
    font-size: 18px;

    &:first-child {
      border-radius: 6px 0 0 6px;
    }

    &:last-of-type {
      border-radius: 0 6px 6px 0;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  input {
    width: 50px;
    border-right: 0;
    border-left: 0;
    outline: none;
    font-size: 13px;
    -moz-appearance: textfield;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      margin: 0;
      -webkit-appearance: none;
    }
  }
`;

const RequestedHint = styled.span`
  margin-left: 12px;
  color: ${COLORS.text};
  font-size: 11px;
  white-space: nowrap;
`;

const AvailableValue = styled.div`
  color: ${COLORS.green};
  font-size: 15px;
  font-weight: 700;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  min-width: 122px;
  padding: 9px 16px;
  color: ${({ $reject }) => ($reject ? COLORS.red : "#fff")};
  background: ${({ $reject }) => ($reject ? "#fff" : COLORS.primary)};
  border: 1px solid ${({ $reject }) => ($reject ? COLORS.red : COLORS.primary)};
  border-radius: 7px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const MotivoInput = styled.input`
  width: 100%;
  margin-top: 12px;
  padding: 9px 10px;
  color: ${COLORS.dark};
  border: 1px solid ${COLORS.border};
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: ${COLORS.primary};
  }
`;

const StatusMessage = styled.div`
  margin-top: 4px;
  padding: 18px 20px 4px;
  color: ${({ $status }) => ($status === "RECUSADO" ? COLORS.red : COLORS.green)};
  border-top: 1px solid ${COLORS.border};
  font-size: 14px;
  font-weight: 700;
`;

const PendingConfirmationMessage = styled.div`
  margin-top: 8px;
  color: ${COLORS.text};
  font-size: 14px;
  font-weight: 600;
`;

const RejectionReason = styled.div`
  margin: 4px 20px 0;
  padding: 16px 0;
  border-top: 1px solid ${COLORS.border};

  ${DetailValue} {
    color: ${COLORS.red};
  }
`;

const EmptyMsg = styled.div`
  padding: 32px 24px;
  color: #777;
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: 0;
  font-size: 15px;
  text-align: center;
`;

const BottomBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 22px 0;
`;

const BackButton = styled.button`
  padding: 8px 24px;
  color: #ffffff;
  background-color: #999;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const formatTipoAtividade = (tipo) => {
  const tipos = {
    ESTAGIO_OBRIGATORIO: "Estágio Obrigatório",
    ESTAGIO_SUPERVISIONADO: "Estágio Supervisionado",
    INTERNATO_MEDICO: "Internato Médico",
    AULA_PRATICA: "Aula Prática",
  };
  return tipos[tipo] || tipo || "-";
};

const parseDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (dateValue, withTime = false) => {
  const date = parseDate(dateValue);
  if (!date) return "-";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const formatPeriodo = (periodos = []) => {
  if (!periodos.length) return "-";
  const ordered = [...periodos].sort(
    (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
  );
  return `${formatDate(ordered[0].dataInicio)} → ${formatDate(ordered[ordered.length - 1].dataFim)}`;
};

const formatDias = (diasSemana = []) => {
  const dias = diasSemana
    .map((dia) => (typeof dia === "string" ? dia : dia?.diaSemana))
    .filter(Boolean);
  return dias.length ? dias.join(", ") : "-";
};

const formatFase = (fase) => {
  const value = String(fase ?? "").trim();
  if (!value) return "-";
  if (/[ªº]$/.test(value) || /\bfase\b/i.test(value)) return value;
  return `${value}ª`;
};

const getStatusIcon = (status) => {
  if (status === "CONCEDIDO" || status === "CONFIRMADA") return concedidoIcon;
  if (status === "RECUSADO") return naoConcedidoIcon;
  return emAnaliseIcon;
};

const getStatusLabel = (status) => {
  if (status === "CONCEDIDO") return "Concedido";
  if (status === "CONFIRMADA") return "Confirmado";
  if (status === "RECUSADO") return "Não Concedido";
  return "Em Análise";
};

const getInitials = (nome = "") => {
  const words = nome.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  return nome.slice(0, 2).toUpperCase() || "--";
};

const renderInstituicaoLogo = (instituicao) => {
  if (!instituicao) return <LogoWrapper>--</LogoWrapper>;
  const logo =
    instituicao.logoUrl ||
    getInstituicaoLogo({
      naturezaJuridica: instituicao.naturezaJuridica,
      esfera: instituicao.esfera,
    });
  const nome = instituicao.sigla || instituicao.nomeRegistroMEC || "Instituição";
  return (
    <LogoWrapper>
      {logo ? <LogoImg src={logo} alt={nome} /> : getInitials(nome)}
    </LogoWrapper>
  );
};

const ConcessaoDetalhe = ({ vaga, onVoltar }) => {
  const popup = usePopup();
  const [vagaData, setVagaData] = useState(vaga);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [quantidades, setQuantidades] = useState({});
  const [motivos, setMotivos] = useState({});
  const [expandedIds, setExpandedIds] = useState({});
  const [downloadingPlanoId, setDownloadingPlanoId] = useState(null);

  const refreshVaga = useCallback(async () => {
    try {
      const result = await apiService.getVagaById(vaga.id);
      setVagaData(result.data ? result.data : result);
    } catch (error) {
      console.error("Erro ao atualizar vaga:", error);
    }
  }, [vaga.id]);

  const fetchSolicitacoes = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.getSolicitacoesVaga(null, vaga.id);
      const data = result.data ? result.data : result;
      const lista = Array.isArray(data) ? data : [];
      setSolicitacoes(lista);
      setQuantidades((current) => {
        const next = { ...current };
        lista.forEach((solicitacao) => {
          if (next[solicitacao.id] === undefined) {
            next[solicitacao.id] = solicitacao.quantidadeAlunos;
          }
        });
        return next;
      });
    } catch (error) {
      console.error("Erro ao carregar solicitações:", error);
      setSolicitacoes([]);
    } finally {
      setLoading(false);
    }
  }, [vaga.id]);

  useEffect(() => {
    fetchSolicitacoes();
  }, [fetchSolicitacoes]);

  const handleConceder = async (solicitacaoId) => {
    const solicitacao = solicitacoes.find((item) => item.id === solicitacaoId);
    const solicitado = Number(solicitacao?.quantidadeAlunos);
    const disponivel = Number(vagaData.quantidadeDisponivel);
    const limite = Number.isFinite(disponivel)
      ? Math.max(0, Math.min(solicitado, disponivel))
      : solicitado;
    const qtd = Math.min(Number(quantidades[solicitacaoId]), limite);
    if (!Number.isInteger(qtd) || qtd <= 0) {
      await popup.alert({
        icon: "warning",
        message:
          limite <= 0
            ? "Não há vagas disponíveis para esta concessão."
            : "Informe uma quantidade válida para conceder.",
      });
      return;
    }

    const confirmed = await popup.confirm({
      icon: "info",
      message: `Deseja conceder ${qtd} vaga(s) para esta solicitação?`,
    });
    if (!confirmed) return;

    setActionLoading((current) => ({ ...current, [solicitacaoId]: "conceder" }));
    try {
      await apiService.concederSolicitacaoVaga(solicitacaoId, qtd);
      await Promise.all([fetchSolicitacoes(), refreshVaga()]);
    } catch (error) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao conceder",
        message: error.message || "Tente novamente.",
      });
    } finally {
      setActionLoading((current) => ({ ...current, [solicitacaoId]: null }));
    }
  };

  const handleRecusar = async (solicitacaoId) => {
    const confirmed = await popup.confirm({
      icon: "info",
      message: "Deseja não conceder esta solicitação?",
    });
    if (!confirmed) return;

    setActionLoading((current) => ({ ...current, [solicitacaoId]: "recusar" }));
    try {
      await apiService.recusarSolicitacaoVaga(solicitacaoId, motivos[solicitacaoId] || null);
      await Promise.all([fetchSolicitacoes(), refreshVaga()]);
    } catch (error) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao recusar",
        message: error.message || "Tente novamente.",
      });
    } finally {
      setActionLoading((current) => ({ ...current, [solicitacaoId]: null }));
    }
  };

  const updateQuantidade = (solicitacao, nextValue) => {
    const limiteSolicitado = Number(solicitacao.quantidadeAlunos) || 1;
    const limiteDisponivel = Number(vagaData.quantidadeDisponivel);
    const maximo = Number.isFinite(limiteDisponivel)
      ? Math.max(0, Math.min(limiteSolicitado, limiteDisponivel))
      : limiteSolicitado;
    const value = maximo === 0 ? 0 : Math.min(maximo, Math.max(1, Number(nextValue) || 1));
    setQuantidades((current) => ({ ...current, [solicitacao.id]: value }));
  };

  const handleDownloadPlanoEnsino = async (solicitacao) => {
    setDownloadingPlanoId(solicitacao.id);
    try {
      await apiService.downloadPlanoEnsino(solicitacao.id);
    } catch (error) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao baixar plano de ensino",
        message: error.message || "Tente novamente.",
      });
    } finally {
      setDownloadingPlanoId(null);
    }
  };

  const unitName = vagaData.estabelecimentoSaude?.nome || "-";
  const vagasTotal = Number(vagaData.quantidade);
  const vagasDisponiveis = Number(vagaData.quantidadeDisponivel);
  const concedidasPorSolicitacao = solicitacoes.reduce(
    (total, solicitacao) =>
      total +
      (["CONCEDIDO", "CONFIRMADA"].includes(solicitacao.status)
        ? Number(solicitacao.quantidadeAlunosConcedidos) || 0
        : 0),
    0
  );
  const vagasConcedidas =
    Number.isFinite(vagasTotal) && Number.isFinite(vagasDisponiveis)
      ? Math.max(vagasTotal - vagasDisponiveis, 0)
      : concedidasPorSolicitacao;

  return (
    <Wrapper>
      <PageHeader>
        <HeaderTitle id="vaga-detail-title">
          {vagaData.unidadeAssistencial?.especialidade?.nome || "Vaga ofertada"}
        </HeaderTitle>
        <HeaderTitle id="requests-title">
          Solicitações para esta vaga
          <RequestCount>{solicitacoes.length}</RequestCount>
        </HeaderTitle>
      </PageHeader>

      <DetailLayout>
        <Column aria-labelledby="vaga-detail-title">
          <VagaCard>
            <VagaTopRow>
              <Avatar>{getInitials(unitName)}</Avatar>
              <UnitText>
                <UnitLabel>Unidade de Saúde:</UnitLabel>
                <UnitName title={unitName}>{unitName}</UnitName>
              </UnitText>
            </VagaTopRow>
            <VagaField>
              <FieldLabel>Área do Curso:</FieldLabel>
              <FieldValue>{vagaData.areaCurso?.nome || "-"}</FieldValue>
            </VagaField>
            <VagaField>
              <FieldLabel>Setor:</FieldLabel>
              <FieldValue>{vagaData.unidadeAssistencial?.nome || "-"}</FieldValue>
            </VagaField>
            <VagaField>
              <FieldLabel>Especialidade:</FieldLabel>
              <FieldValue>{vagaData.unidadeAssistencial?.especialidade?.nome || "-"}</FieldValue>
            </VagaField>
            <VagaField>
              <FieldLabel>Turno:</FieldLabel>
              <FieldValue>{vagaData.turno || "-"}</FieldValue>
            </VagaField>
            <VagaField>
              <FieldLabel>Tipo de Atividade:</FieldLabel>
              <FieldValue>{formatTipoAtividade(vagaData.tipoAtividade)}</FieldValue>
            </VagaField>
          </VagaCard>

          <TotalsCard aria-label="Resumo das vagas">
            <TotalItem $color={COLORS.orange}>
              <strong>{Number.isFinite(vagasTotal) ? vagasTotal : "-"}</strong>
              Total
            </TotalItem>
            <TotalItem $color={COLORS.green}>
              <strong>{Number.isFinite(vagasDisponiveis) ? vagasDisponiveis : "-"}</strong>
              Disponíveis
            </TotalItem>
            <TotalItem $color={COLORS.green}>
              <strong>{vagasConcedidas}</strong>
              Concedidas
            </TotalItem>
          </TotalsCard>

          <BottomBar>
            <BackButton type="button" onClick={onVoltar}>Voltar</BackButton>
          </BottomBar>
        </Column>

        <Column aria-labelledby="requests-title">
          {loading ? (
            <EmptyMsg>Carregando solicitações...</EmptyMsg>
          ) : solicitacoes.length === 0 ? (
            <EmptyMsg>Nenhuma solicitação encontrada para esta vaga.</EmptyMsg>
          ) : (
            <RequestsList>
              {solicitacoes.map((solicitacao) => {
                const status = solicitacao.status || "PENDENTE";
                const isPendente = status === "PENDENTE";
                const isExpanded = Boolean(expandedIds[solicitacao.id]);
                const isLoading = actionLoading[solicitacao.id];
                const instituicao = solicitacao.curso?.campus?.instituicoes?.[0];
                const nomeInstituicao =
                  instituicao?.sigla || instituicao?.nomeRegistroMEC || "-";
                const faseFormatada = formatFase(solicitacao.fase);
                const limiteSolicitado = Number(solicitacao.quantidadeAlunos) || 1;
                const maximoConcessao = Number.isFinite(vagasDisponiveis)
                  ? Math.max(0, Math.min(limiteSolicitado, vagasDisponiveis))
                  : limiteSolicitado;
                const quantidade =
                  maximoConcessao === 0
                    ? 0
                    : Math.min(
                        maximoConcessao,
                        Math.max(1, Number(quantidades[solicitacao.id]) || 1)
                      );
                const disponiveisDepois = Number.isFinite(vagasDisponiveis)
                  ? Math.max(vagasDisponiveis - quantidade, 0)
                  : "-";
                const totalHoras =
                  solicitacao.quantidadeAlunos != null && solicitacao.horasTotaisPorAluno != null
                    ? Number(solicitacao.quantidadeAlunos) * Number(solicitacao.horasTotaisPorAluno)
                    : null;
                const bodyId = `solicitacao-${solicitacao.id}-detalhes`;

                return (
                  <RequestCard key={solicitacao.id} $expanded={isExpanded}>
                    <RequestSummary $expanded={isExpanded}>
                      <InstitutionSummary>
                        <StatusIcon
                          src={getStatusIcon(status)}
                          alt={getStatusLabel(status)}
                          title={getStatusLabel(status)}
                        />
                        {renderInstituicaoLogo(instituicao)}
                        <InstitutionText>
                          <InstitutionName title={nomeInstituicao}>{nomeInstituicao}</InstitutionName>
                          <CourseLine title={`${solicitacao.curso?.curso || "-"} • ${solicitacao.nomeDisciplina || "-"} • ${faseFormatada}`}>
                            {solicitacao.curso?.curso || "-"} &nbsp;•&nbsp; {solicitacao.nomeDisciplina || "-"} &nbsp;•&nbsp; {faseFormatada}
                          </CourseLine>
                        </InstitutionText>
                      </InstitutionSummary>

                      <SummaryField>
                        <SummaryLabel>Docente</SummaryLabel>
                        <SummaryValue title={solicitacao.docenteResponsavel || "-"}>
                          {solicitacao.docenteResponsavel || "-"}
                        </SummaryValue>
                      </SummaryField>
                      <SummaryField>
                        <SummaryLabel>Solicitou</SummaryLabel>
                        <SummaryValue>{solicitacao.quantidadeAlunos ?? "-"} alunos</SummaryValue>
                      </SummaryField>
                      <SummaryField className="request-date">
                        <SummaryLabel>Solicitada em</SummaryLabel>
                        <SummaryValue>{formatDate(solicitacao.createdAt, true)}</SummaryValue>
                      </SummaryField>
                      <ExpandButton
                        type="button"
                        $expanded={isExpanded}
                        aria-expanded={isExpanded}
                        aria-controls={bodyId}
                        aria-label={`${isExpanded ? "Recolher" : "Expandir"} solicitação de ${nomeInstituicao}`}
                        onClick={() =>
                          setExpandedIds((current) => ({
                            ...current,
                            [solicitacao.id]: !current[solicitacao.id],
                          }))
                        }
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">expand_more</span>
                      </ExpandButton>
                    </RequestSummary>

                    {isExpanded && (
                      <RequestBody id={bodyId}>
                        <DetailsGrid>
                          <DetailField>
                            <DetailLabel>Período</DetailLabel>
                            <DetailValue>{formatPeriodo(solicitacao.periodos)}</DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>CPF do docente</DetailLabel>
                            <DetailValue>
                              {solicitacao.cpfDocenteResponsavel
                                ? formatCPF(solicitacao.cpfDocenteResponsavel)
                                : "-"}
                            </DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>Tipo de Atividade</DetailLabel>
                            <DetailValue>{formatTipoAtividade(vagaData.tipoAtividade)}</DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>Nº de Alunos / unidade</DetailLabel>
                            <DetailValue>{solicitacao.quantidadeAlunos ?? "-"}</DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>Dias</DetailLabel>
                            <DetailValue>{formatDias(solicitacao.diasSemana)}</DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>Nº de horas / aluno por unidade</DetailLabel>
                            <DetailValue>
                              {solicitacao.horasTotaisPorAluno != null
                                ? `${solicitacao.horasTotaisPorAluno}h`
                                : "-"}
                            </DetailValue>
                          </DetailField>
                          <DetailField>
                            <DetailLabel>Nº Total de horas</DetailLabel>
                            <DetailValue>{totalHoras != null ? `${totalHoras}h` : "-"}</DetailValue>
                          </DetailField>
                        </DetailsGrid>

                        {solicitacao.planoEnsinoId && (
                          <PlanoEnsinoButton
                            type="button"
                            onClick={() => handleDownloadPlanoEnsino(solicitacao)}
                            disabled={downloadingPlanoId === solicitacao.id}
                          >
                            {downloadingPlanoId === solicitacao.id
                              ? "Baixando..."
                              : "Baixar plano de ensino"}
                          </PlanoEnsinoButton>
                        )}

                        {status === "RECUSADO" && solicitacao.motivoNegacao && (
                          <RejectionReason>
                            <DetailLabel>Motivo da não concessão</DetailLabel>
                            <DetailValue>{solicitacao.motivoNegacao}</DetailValue>
                          </RejectionReason>
                        )}

                        {isPendente ? (
                          <DecisionRow>
                            <div>
                              <DetailLabel>Vagas a conceder:</DetailLabel>
                              <QuantityControl>
                                <button
                                  type="button"
                                  aria-label="Diminuir vagas"
                                  onClick={() => updateQuantidade(solicitacao, quantidade - 1)}
                                  disabled={Boolean(isLoading) || quantidade <= 1 || maximoConcessao === 0}
                                >
                                  −
                                </button>
                                <input
                                  aria-label="Quantidade de vagas a conceder"
                                  type="number"
                                  min={maximoConcessao === 0 ? 0 : 1}
                                  max={maximoConcessao}
                                  value={quantidade}
                                  onChange={(event) => updateQuantidade(solicitacao, event.target.value)}
                                  disabled={Boolean(isLoading) || maximoConcessao === 0}
                                />
                                <button
                                  type="button"
                                  aria-label="Aumentar vagas"
                                  onClick={() => updateQuantidade(solicitacao, quantidade + 1)}
                                  disabled={
                                    Boolean(isLoading) ||
                                    maximoConcessao === 0 ||
                                    quantidade >= maximoConcessao
                                  }
                                >
                                  +
                                </button>
                                <RequestedHint>(solicitado: {solicitacao.quantidadeAlunos})</RequestedHint>
                              </QuantityControl>
                              <MotivoInput
                                aria-label="Motivo para não concessão"
                                placeholder="Motivo (opcional, para não concessão)"
                                value={motivos[solicitacao.id] || ""}
                                onChange={(event) =>
                                  setMotivos((current) => ({
                                    ...current,
                                    [solicitacao.id]: event.target.value,
                                  }))
                                }
                                disabled={Boolean(isLoading)}
                              />
                            </div>
                            <div>
                              <DetailLabel>Disponíveis após concessão:</DetailLabel>
                              <AvailableValue>
                                {disponiveisDepois} {disponiveisDepois === 1 ? "vaga" : "vagas"}
                              </AvailableValue>
                            </div>
                            <ActionButtons>
                              <ActionButton
                                type="button"
                                $reject
                                onClick={() => handleRecusar(solicitacao.id)}
                                disabled={Boolean(isLoading)}
                              >
                                {isLoading === "recusar" ? "Recusando..." : "Não Conceder ✕"}
                              </ActionButton>
                              <ActionButton
                                type="button"
                                onClick={() => handleConceder(solicitacao.id)}
                                disabled={Boolean(isLoading) || maximoConcessao === 0}
                              >
                                {isLoading === "conceder" ? "Concedendo..." : "Conceder ✓"}
                              </ActionButton>
                            </ActionButtons>
                          </DecisionRow>
                        ) : (
                          <StatusMessage $status={status}>
                            {getStatusLabel(status)}
                            {["CONCEDIDO", "CONFIRMADA"].includes(status) &&
                            solicitacao.quantidadeAlunosConcedidos != null
                              ? ` • ${solicitacao.quantidadeAlunosConcedidos} vaga(s) concedida(s)`
                              : ""}
                            {solicitacao.dataConcedida
                              ? ` • Decisão em ${formatDate(solicitacao.dataConcedida, true)}`
                              : ""}
                            {status === "CONCEDIDO" && (
                              <PendingConfirmationMessage>
                                Aguardando confirmação pelo coordenador de estágio/ curso
                              </PendingConfirmationMessage>
                            )}
                          </StatusMessage>
                        )}
                      </RequestBody>
                    )}
                  </RequestCard>
                );
              })}
            </RequestsList>
          )}
        </Column>
      </DetailLayout>
    </Wrapper>
  );
};

export default ConcessaoDetalhe;
